import { type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

import { PlotTooltip } from './PlotTooltip'

/**
 * DivergingBar — one centered diverging Likert row rendered as per-segment ROUNDED
 * elements separated by ~2px card-surface gaps, replacing the legacy single
 * `overflow-hidden rounded-md` container of butt-jointed segments.
 *
 * Data model is UNCHANGED (the existing 3-band Top-2 / Neutral / Bottom-2 stack): the
 * caller hands each band a pre-computed container-% `leftPct`/`widthPct` (from
 * `divergingLayout`) plus its color and tooltip value/label. This primitive is band-count
 * agnostic — it just paints whatever segments it is given.
 *
 * GAP-AWARE ZERO LINE (critical): each segment is absolutely positioned by its EXACT
 * logical `leftPct`/`widthPct`, then the ~2px inter-segment gap is carved SYMMETRICALLY
 * from inside each box (`left + gap/2`, `width − gap`). Because the inset is symmetric, a
 * segment's rendered center equals its logical center, so the neutral band's midpoint
 * lands exactly on the shared zero line (`zeroLinePct`, default 50%) and every row's zero
 * line stacks in one column. This is why the layout math is NOT re-derived gap-aware — the
 * gap is a render-time inset that provably preserves the midpoint, not an added track width.
 *
 * Each segment is wrapped in the shared `PlotTooltip` (value-leads-label-follows, hover +
 * keyboard-focus parity), replacing the scattered native `title=` attributes. The tooltip
 * wrapper is stretched to fill its positioned slot via a single-child CSS grid cell, so
 * `PlotTooltip` needs no extra sizing props.
 */
export interface DivergingBarSegment {
  /** Container-% offset of the segment's left edge (from `divergingLayout`). */
  leftPct: number
  /** Container-% width of the segment (from `divergingLayout`). */
  widthPct: number
  /** Fill color — an `hsl(var(--token))` string (never a raw hex). */
  color: string
  /** Leading value for the tooltip (e.g. "21%"). */
  value: ReactNode
  /** Trailing label for the tooltip (e.g. "Neutral"). */
  label: ReactNode
  /** Accessible name for the segment's focusable tooltip trigger. Defaults to
   *  "value · label" when both are plain strings. */
  ariaLabel?: string
  /** Optional per-segment testid on the painted fill. */
  'data-testid'?: string
  /** Optional in-band caption (percent/count). Tooltip-only is not enough on
   *  low-contrast fills — the light green is 2.48:1 on the report surface. */
  caption?: ReactNode
}

export interface DivergingBarProps {
  /** The bands in visual order (low → [neutral] → high). Zero-width bands are skipped. */
  segments: Array<DivergingBarSegment>
  /** Accessible summary of the whole bar (role="img"). */
  ariaLabel: string
  /** Container-% of the shared zero line (same for every row so they align). Default 50. */
  zeroLinePct?: number
  /** Inter-segment gap in px, carved symmetrically from each segment. Default 2. */
  gapPx?: number
  /** Track height/extra classes. Default `h-6`. */
  className?: string
  /** Rounded-end class for each segment. Default `rounded-[3px]`. */
  segmentClassName?: string
  /** Zero-line class (color/width). Default a thin muted line. */
  zeroLineClassName?: string
  /** Testid for the track container. */
  'data-testid'?: string
}

export function DivergingBar(props: DivergingBarProps) {
  const {
    segments,
    ariaLabel,
    zeroLinePct = 50,
    gapPx = 2,
    className,
    segmentClassName,
    zeroLineClassName,
    'data-testid': dataTestId,
  } = props

  const halfGap = gapPx / 2

  return (
    // `role="group"` (not `img`): an img container prunes its subtree for assistive tech
    // while the PlotTooltip triggers inside stay keyboard-focusable — dead tab stops. A
    // group keeps the summary label AND lets each labeled segment trigger announce itself.
    <div
      // `flex-1` assumes a ROW flex parent (its usual width-filling role at existing call
      // sites). Mounted inside a COLUMN flex parent, `flex-basis: 0%` applies to height
      // instead and collapses the track to 0px even when `className` passes an explicit
      // height. Callers in a column layout must wrap this component in a block container
      // with a fixed height (e.g. `<div className="relative h-8">`, per MatrixChart/NumericChart).
      className={cn('relative h-6 flex-1', className)}
      role="group"
      aria-label={ariaLabel}
      data-testid={dataTestId}
    >
      {segments.map((seg, i) => {
        if (!(seg.widthPct > 0)) {
          return null
        }
        const slotStyle: CSSProperties = {
          left: `calc(${seg.leftPct}% + ${halfGap}px)`,
          width: `max(0px, calc(${seg.widthPct}% - ${gapPx}px))`,
        }
        const triggerAriaLabel =
          seg.ariaLabel ??
          (typeof seg.value === 'string' && typeof seg.label === 'string'
            ? `${seg.value} · ${seg.label}`
            : undefined)
        return (
          <div key={i} className="absolute inset-y-0 grid" style={slotStyle}>
            <PlotTooltip value={seg.value} label={seg.label} triggerAriaLabel={triggerAriaLabel}>
              <span
                className={cn(
                  'flex h-full w-full items-center justify-center overflow-hidden',
                  segmentClassName ?? 'rounded-[3px]',
                )}
                style={{ background: seg.color }}
                data-testid={seg['data-testid']}
              >
                {seg.caption != null ? seg.caption : null}
              </span>
            </PlotTooltip>
          </div>
        )
      })}
      {/* The fixed, shared zero line — same x for every row, so rows align in a column. */}
      <span
        aria-hidden
        className={cn('pointer-events-none absolute inset-y-0 w-px -translate-x-1/2', zeroLineClassName ?? 'bg-foreground/30')}
        style={{ left: `${zeroLinePct}%` }}
      />
    </div>
  )
}
